import { useEffect, useState } from "react";
import { format, addDays } from "date-fns";
import { CalendarIcon, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, inr } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import Stepper from "@/components/Stepper";

const ymd = (d) => format(d, "yyyy-MM-dd");
const toDate = (s) => (s ? new Date(`${s}T00:00`) : undefined);
const today = () => new Date(new Date().setHours(0, 0, 0, 0));

function DatePick({ value, onChange, testid }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="h-10 w-full rounded-md border border-[#E2DACF] px-3 flex items-center gap-2 text-sm bg-white" data-testid={testid}>
          <CalendarIcon size={15} className="text-[#5C6B63]"/>{value ? format(toDate(value), "dd MMM yyyy") : "Pick date"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-auto" align="start">
        <Calendar mode="single" selected={toDate(value)} onSelect={(d) => d && onChange(ymd(d))} disabled={(d) => d < today()}/>
      </PopoverContent>
    </Popover>
  );
}

function SlotPicker({ slots, value, onChange, needVehicles, showDate }) {
  if (!slots) return null;
  if (!slots.length) return <p className="text-sm text-[#5C6B63]">No departures open for this date.</p>;
  if (slots.every((s) => s.started)) return <p className="text-sm text-[#C86D51] font-medium" data-testid="modify-all-started">Safari started — no slots left for this date.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {slots.map((s) => {
        const ok = (needVehicles ? s.remaining_vehicles : s.remaining_seats) > 0 || s.id === value;
        return (
          <button key={s.id} type="button" disabled={!ok} onClick={() => onChange(s.id)} data-testid={`modify-slot-${s.id}`}
            className={`px-3 py-1.5 rounded-full text-sm border flex items-center gap-1 ${value === s.id ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "border-[#E2DACF] bg-white"} ${!ok ? "opacity-40" : ""}`}>
            <Clock size={12}/>{showDate ? `${format(toDate(s.date), "dd MMM")} · ` : ""}{s.departure_time} {s.label}{s.started ? " · safari started" : ""}
          </button>
        );
      })}
    </div>
  );
}

function useSafariSlots(safariId, date) {
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    if (!safariId || !date) return;
    api.get(`/safaris/${safariId}/availability`, { params: { date } }).then((r) => setSlots(r.data)).catch(() => setSlots([]));
  }, [safariId, date]);
  return slots;
}

function usePackageSlots(packageId, checkIn) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!packageId || !checkIn) return;
    api.get(`/packages/${packageId}/availability`, { params: { check_in: checkIn } }).then((r) => setData(r.data)).catch(() => setData({ slots: [] }));
  }, [packageId, checkIn]);
  return data;
}

function ChangeFields({ b, ch, set }) {
  const kind = b.kind || "safari";
  const safariSlots = useSafariSlots(kind === "safari" ? b.safari_id : null, ch.date);
  const pkg = usePackageSlots(kind === "package" ? b.package_id : null, ch.check_in);
  const [range, setRange] = useState({ from: toDate(b.check_in), to: toDate(b.check_out) });

  return (
    <div className="space-y-4">
      {kind === "safari" && (
        <>
          <div><label className="jt-eyebrow mb-2 block">New date</label>
            <DatePick value={ch.date} onChange={(d) => set({ date: d, slot_id: "" })} testid="modify-date"/></div>
          <div><label className="jt-eyebrow mb-2 block">Departure</label>
            <SlotPicker slots={safariSlots} value={ch.slot_id} onChange={(id) => set({ slot_id: id })} needVehicles={b.booking_type === "private"}/></div>
          {b.booking_type === "private" && <p className="text-sm text-[#5C6B63]" data-testid="modify-vehicles">Jeeps: <strong className="text-[#1C2421]">{Math.max(1, Math.ceil((ch.adults + ch.children) / 6))}</strong> · set automatically, 1 jeep per 6 guests</p>}
        </>
      )}
      {kind === "stay" && (
        <>
          <div><label className="jt-eyebrow mb-2 block">New dates</label>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className="h-10 w-full rounded-md border border-[#E2DACF] px-3 flex items-center gap-2 text-sm bg-white" data-testid="modify-stay-dates">
                  <CalendarIcon size={15}/>{ch.check_in} → {ch.check_out}
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-auto" align="start">
                <Calendar mode="range" numberOfMonths={2} selected={range} disabled={(d) => d < today()}
                  onSelect={(r) => { setRange(r); if (r?.from && r?.to) set({ check_in: ymd(r.from), check_out: ymd(r.to) }); }}/>
              </PopoverContent>
            </Popover></div>
          <Stepper label="Rooms" value={ch.rooms} setValue={(v) => set({ rooms: v })} min={1} max={10} testid="modify-rooms"/>
        </>
      )}
      {kind === "package" && (
        <>
          <div><label className="jt-eyebrow mb-2 block">New check-in</label>
            <DatePick value={ch.check_in} onChange={(d) => set({ check_in: d, slot_id: "" })} testid="modify-checkin"/></div>
          <div><label className="jt-eyebrow mb-2 block">Safari during stay</label>
            <SlotPicker slots={pkg?.slots} value={ch.slot_id} onChange={(id) => set({ slot_id: id })} needVehicles showDate/></div>
        </>
      )}
      <Stepper label="Adults" value={ch.adults} setValue={(v) => set({ adults: v })} min={1} testid="modify-adults"/>
      <Stepper label="Children" value={ch.children} setValue={(v) => set({ children: v })} testid="modify-children"/>
    </div>
  );
}

function initialChange(b) {
  return { date: b.slot_date, slot_id: b.slot_id, vehicles: b.vehicles || 1, check_in: b.check_in, check_out: b.check_out,
    rooms: b.rooms || 1, adults: b.adults, children: b.children };
}

function toPayload(b, ch) {
  const kind = b.kind || "safari";
  const p = { adults: ch.adults, children: ch.children };
  if (kind === "safari") Object.assign(p, { slot_id: ch.slot_id }, b.booking_type === "private" ? { vehicles: ch.vehicles } : {});
  if (kind === "stay") Object.assign(p, { check_in: ch.check_in, check_out: ch.check_out, rooms: ch.rooms });
  if (kind === "package") Object.assign(p, { check_in: ch.check_in, slot_id: ch.slot_id });
  return p;
}

/** quoteFn(payload) and submitFn(payload) return axios promises; admin and guest pass different endpoints. */
export function ModifyDialog({ booking, open, onOpenChange, quoteFn, submitFn, onDone, note }) {
  const [ch, setCh] = useState(initialChange(booking));
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setCh(initialChange(booking)); setQuote(null); } }, [open, booking]);
  const set = (patch) => { setCh((c) => ({ ...c, ...patch })); setQuote(null); };
  const ready = (booking.kind || "safari") === "stay" ? ch.check_in && ch.check_out : ch.slot_id;

  const check = async () => {
    setBusy(true);
    try { setQuote((await quoteFn(toPayload(booking, ch))).data); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try { const { data } = await submitFn(toPayload(booking, ch)); toast.success("Booking updated"); onDone(data); onOpenChange(false); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="modify-dialog">
        <DialogHeader><DialogTitle>Change booking {booking.booking_ref}</DialogTitle></DialogHeader>
        {note && <p className="text-xs text-[#5C6B63] -mt-2">{note}</p>}
        <ChangeFields b={booking} ch={ch} set={set}/>
        {quote && (
          <div className="bg-[#F5F0EB] rounded-xl p-4 text-sm space-y-1" data-testid="modify-quote">
            {quote.quote.summary.map(([k, v]) => <div key={k} className="flex justify-between gap-4"><span className="text-[#5C6B63]">{k}</span><span className="font-medium text-right">{v}</span></div>)}
            <hr className="my-2 border-[#E2DACF]"/>
            <div className="flex justify-between"><span>Current</span><span>{inr(quote.old_amount)}</span></div>
            <div className="flex justify-between font-semibold"><span>New total</span><span>{inr(quote.new_amount)}</span></div>
            <div className={`flex justify-between font-semibold ${quote.difference < 0 ? "text-[#1E3A2B]" : quote.difference > 0 ? "text-[#C86D51]" : ""}`} data-testid="modify-difference">
              <span>{quote.difference < 0 ? "Refund to you" : quote.difference > 0 ? "To pay" : "Difference"}</span><span>{inr(Math.abs(quote.difference))}</span>
            </div>
            {!quote.allowed && <p className="text-[#C86D51] text-xs pt-2" data-testid="modify-not-allowed">{quote.message}</p>}
          </div>
        )}
        <DialogFooter className="gap-2">
          <button onClick={check} disabled={!ready || busy} className="jt-pill-outline text-sm disabled:opacity-40" data-testid="modify-check-btn">Check new price</button>
          <button onClick={confirm} disabled={!quote?.allowed || busy} className="jt-pill-primary text-sm disabled:opacity-40" data-testid="modify-confirm-btn">Confirm change</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CancelDialog({ booking, open, onOpenChange, previewFn, submitFn, onDone, admin }) {
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");
  const [refund, setRefund] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setReason("");
    previewFn().then((r) => { setPreview(r.data); setRefund(String(r.data.refund_amount)); }).catch((e) => toast.error(formatErr(e)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await submitFn({ reason, ...(admin ? { refund_amount: Number(refund) } : {}) });
      toast.success("Booking cancelled"); onDone(data); onOpenChange(false);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cancel-dialog">
        <DialogHeader><DialogTitle>Cancel booking {booking.booking_ref}?</DialogTitle></DialogHeader>
        {preview && (
          <div className="bg-[#F5F0EB] rounded-xl p-4 text-sm space-y-1" data-testid="cancel-preview">
            <div className="flex justify-between"><span className="text-[#5C6B63]">Booking value</span><span>{inr(booking.amount)}</span></div>
            <div className="flex justify-between"><span className="text-[#5C6B63]">Time to start</span><span>{preview.hours_left > 0 ? `${Math.floor(preview.hours_left)} hours` : "Started"}</span></div>
            <div className="flex justify-between font-semibold"><span>Refund ({preview.refund_pct}%)</span><span data-testid="cancel-refund-amount">{inr(preview.refund_amount)}</span></div>
            <p className="text-xs text-[#5C6B63] pt-1">Policy: 100% refund 48h+ before start · 50% between 24–48h · none within 24h.</p>
          </div>
        )}
        {admin && (
          <div><label className="jt-eyebrow mb-2 block">Refund amount (override)</label>
            <Input type="number" value={refund} onChange={(e) => setRefund(e.target.value)} data-testid="cancel-refund-input"/></div>
        )}
        <div><label className="jt-eyebrow mb-2 block">Reason (optional)</label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason"/></div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="jt-pill-outline text-sm">Keep booking</button>
          <button onClick={go} disabled={busy} className="jt-pill text-sm bg-[#A33A2A] text-white hover:bg-[#8A2F22] disabled:opacity-40" data-testid="cancel-confirm-btn">Cancel booking</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const tomorrow = () => ymd(addDays(new Date(), 1));
